// Helpers for reading split-part package rows off student_package_table.
//
// Money is derived from the part rather than stored: gross comes from the
// customPlan levelList, payable from split_total. That keeps the list correct if
// the split calculation itself changes later.

export type SplitLevel = {
  levelId?: string;
  level?: string;
  classCount?: number;
  unit_price?: number;
};

export type SplitPart = {
  id?: string;
  createdOn?: string;
  package_id?: string;
  packageLink?: string;
  split_package_id?: string;
  split_group_id?: string;
  split_part?: number;
  split_parts_total?: number;
  split_total?: number;
  payment_link_status?: string;
  orderId?: string | null;
  performedBy?: string;
  teacherName?: string;
  currency?: string;
  courseData?: any[];
  customPlan?: { levelList?: SplitLevel[] };
  planList?: any[];
  [key: string]: any;
};

export type SplitGroup = {
  groupId: string;
  courseName: string;
  teacherName: string;
  createdOn: string;
  parts: SplitPart[];
  collected: number;
  total: number;
};

export type PartStatus = "paid" | "external" | "pending";

const SETTLED = ["paid", "external"];

export function getPartStatus(part: SplitPart): PartStatus {
  const status = String(part.payment_link_status || "").toLowerCase();
  if (status === "external") return "external";
  if (status === "paid" || part.orderId) return "paid";
  return "pending";
}

export function isSettled(part: SplitPart): boolean {
  return SETTLED.includes(String(part.payment_link_status || "").toLowerCase()) || Boolean(part.orderId);
}

export function getLevels(part: SplitPart): SplitLevel[] {
  if (part.customPlan?.levelList?.length) return part.customPlan.levelList;
  // parts created before the customPlan shape landed
  const legacy = part.planList?.[0]?.classList?.[0];
  return legacy ? [{ level: legacy.level, classCount: legacy.classCount, unit_price: legacy.unit_price }] : [];
}

export function describeLevels(part: SplitPart): string {
  const levels = getLevels(part);
  if (!levels.length) return "—";
  return levels
    .map((l) => `${l.level} ${l.classCount} ${Number(l.classCount) === 1 ? "class" : "classes"}`)
    .join(", ");
}

export function getClassCount(part: SplitPart): number {
  return getLevels(part).reduce((sum, l) => sum + (Number(l.classCount) || 0), 0);
}

/** Undiscounted value of the classes in this part. */
export function getGross(part: SplitPart): number {
  return getLevels(part).reduce(
    (sum, l) => sum + (Number(l.classCount) || 0) * (Number(l.unit_price) || 0),
    0
  );
}

export function getPayable(part: SplitPart): number {
  return Number(part.split_total) || 0;
}

/** Never negative - a part priced above its raw class value shows no discount. */
export function getDiscount(part: SplitPart): number {
  return Math.max(getGross(part) - getPayable(part), 0);
}

/** The part's effective rate - what is stored as specialDiscountPercentage. */
export function getDiscountPercent(part: SplitPart): number {
  const gross = getGross(part);
  return gross > 0 ? (getDiscount(part) / gross) * 100 : 0;
}

// Symbols for the currencies Artium prices in. Anything unrecognised falls back
// to its own code, which is always correct even if less compact.
const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  SGD: "S$",
  AED: "AED ",
};

export function getCurrencyCode(currency?: string): string {
  return String(currency || "INR").toUpperCase();
}

export function formatMoney(value: number, currency?: string): string {
  const code = getCurrencyCode(currency);
  const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `;
  // INR groups in lakhs; everything else in thousands
  const locale = code === "INR" ? "en-IN" : "en-US";
  return `${symbol}${Math.round(value).toLocaleString(locale)}`;
}

/** Date-only, for link expiry. Returns "" when the part has no expiry set. */
export function formatExpiry(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  const month = date.toLocaleString("en-US", { month: "short" });
  return `${month} ${date.getDate()} ${date.getFullYear()}`;
}

/** Expiry is end-of-day, so a link is live for the whole of its expiry date. */
export function isExpired(part: SplitPart): boolean {
  if (!part.expiryDate) return false;
  const date = new Date(part.expiryDate);
  if (isNaN(date.getTime())) return false;
  date.setHours(23, 59, 59, 999);
  return date.getTime() < Date.now();
}

export function formatCreatedOn(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  const month = date.toLocaleString("en-US", { month: "short" });
  const time = date.toLocaleString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${month} ${date.getDate()} ${date.getFullYear()}, ${time}`;
}

/** Groups a student's package rows into split groups, newest group first. */
export function groupSplitParts(items: SplitPart[]): SplitGroup[] {
  const splitRows = (items || []).filter((item) => item && item.split_group_id);
  const byGroup = new Map<string, SplitPart[]>();

  splitRows.forEach((row) => {
    const key = String(row.split_group_id);
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key)!.push(row);
  });

  const groups: SplitGroup[] = [];
  byGroup.forEach((parts, groupId) => {
    parts.sort((a, b) => (Number(a.split_part) || 0) - (Number(b.split_part) || 0));
    const first = parts[0] || {};
    groups.push({
      groupId,
      courseName: first.courseData?.[0]?.courseName || "—",
      teacherName: first.teacherName || "—",
      createdOn: parts.reduce(
        (earliest, p) => (!earliest || (p.createdOn || "") < earliest ? p.createdOn || "" : earliest),
        ""
      ),
      parts,
      collected: parts.filter(isSettled).reduce((sum, p) => sum + getPayable(p), 0),
      total: parts.reduce((sum, p) => sum + getPayable(p), 0),
    });
  });

  return groups.sort((a, b) => (b.createdOn || "").localeCompare(a.createdOn || ""));
}
